import { useState } from "react";
import {
  makeGetApiCall,
  makePostApiCall,
  makePutApiCall,
} from "../../shared/makeApiCall";
import DeleteConfirmationForm from "../../components/forms/DeleteConfirmationForm";
import DeleteReceivingItemByBarcodeForm from "../../components/forms/receiving/DeleteReceivingItemByBarcodeForm";
import { updateObjectInArray } from "../../shared/utility";
import {
  buildReceivingInvoicePayload,
  RECEIVING_MESSAGES,
} from "../../shared/receivingInvoices";
import { createSnackbarHelpers } from "../../shared/snackbar";

export function ReceivingInvoiceDetailsViewModel(
  logout,
  receivingId,
  addSnackbar,
) {
  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null,
  });
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [truckOptions, setTruckOptions] = useState([]);
  const [supplierOptions, setSupplierOptions] = useState([]);
  const [productOptions, setProductOptions] = useState([]);
  const [carrierOptions, setCarrierOptions] = useState([]);
  const [poOptions, setPoOptions] = useState([]);
  const [receiving, setReceiving] = useState({ status: 0 });
  const [selectedItems, setSelectedItems] = useState(null);
  const [rowDetails, setRowDetails] = useState({}); // { [rowId]: detailData }
  const [loadingRows, setLoadingRows] = useState({}); // { [rowId]: boolean }
  const [subRowDetails, setSubRowDetails] = useState({}); // { [rowId]: detailData }
  const [subLoadingRows, setSubLoadingRows] = useState({}); // { [rowId]: boolean }
  const [lastRow, setLastRow] = useState(null);
  const [loading, setLoading] = useState(false);

  const { showSuccess, showError } = createSnackbarHelpers(addSnackbar);

  const getReceivingInvoiceDetails = async function (page = 1) {
    setLoading(true);
    const endpoint =
      page && page > 1
        ? `receivingInvoices/${receivingId}?page=${page}`
        : `receivingInvoices/${receivingId}`;
    let callResult = await makeGetApiCall(endpoint);

    if (callResult.success) {
      setReceiving(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showError(RECEIVING_MESSAGES.loadFailed);
    }
    setLoading(false);
  };

  const updateReceivingInvoice = async function (payload) {
    let callResult = await makePostApiCall(
      `updateReceivingInvoices/${receiving.id}`,
      buildReceivingInvoicePayload(payload),
      "multipart/form-data",
    );

    if (callResult.success) {
      await getReceivingInvoiceDetails();
      showSuccess(RECEIVING_MESSAGES.updated);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showError(RECEIVING_MESSAGES.updateFailed);
    }
    return callResult;
  };

  const addItemToReceiving = async function (values) {
    const payload = {
      invoice_id: receiving.id,
      lot_number: receiving.lot_number,
      ...values,
    };
    let callResult = await makePostApiCall("receivingItems/create", payload);

    if (callResult.success) {
      setEmptyModelOpen({
        open: false,
        childComponent: null,
      });
      const currReceivingItems = receiving.items ? receiving.items : [];
      setReceiving({
        ...receiving,
        items: [callResult.data.item, ...currReceivingItems],
      });
      showSuccess(RECEIVING_MESSAGES.itemAdded);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showError(RECEIVING_MESSAGES.itemAddFailed);
    }
    return callResult;
  };

  const getProductOptionsForDropdown = async function () {
    let callResult = await makeGetApiCall(`productsForDropdown`);

    if (callResult.success) {
      setProductOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const getEmployeeOptionsForDropdown = async function () {
    let callResult = await makeGetApiCall(`usersForDropdown`);

    if (callResult.success) {
      setEmployeeOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showError(RECEIVING_MESSAGES.loadEmployeesFailed);
    }
  };

  const getTruckOptionsForDropdown = async function () {
    let callResult = await makeGetApiCall(`allReceivingTrucksForDropdown`);

    if (callResult.success) {
      setTruckOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showError(RECEIVING_MESSAGES.loadTrucksFailed);
    }
  };

  const getSupplierOptionsForDropdown = async function () {
    let callResult = await makeGetApiCall(`suppliersForDropdown`);

    if (callResult.success) {
      setSupplierOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showError(RECEIVING_MESSAGES.loadSuppliersFailed);
    }
  };

  const onFetchProductDetails = async function (productId) {
    let callResult = await makeGetApiCall(`products/${productId}`);

    if (!callResult.success) {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showError(RECEIVING_MESSAGES.loadProductFailed);
    }
    return callResult;
  };

  const deleteReceivingItemById = async function (deletedItem) {
    let callResult = await makePostApiCall(
      `deleteReceivingItem/${deletedItem.id}`,
    );
    if (callResult.success) {
      setEmptyModelOpen({
        open: false,
        childComponent: null,
      });

      const currentItems = [...receiving.items];

      var itemIndex = currentItems.findIndex(
        (it) =>
          it.plu === deletedItem.plu &&
          it.lot_number === deletedItem.lot_number,
      );
      var itemToUpdate = currentItems[itemIndex];

      const newSubItems = itemToUpdate.subItems.filter(
        (it) => it.id !== deletedItem.id,
      );

      itemToUpdate = {
        ...itemToUpdate,
        carton_count: itemToUpdate.carton_count - 1,
        subItems: newSubItems,
      };

      var payload = {
        index: itemIndex,
        item: itemToUpdate,
      };
      const newItems = updateObjectInArray(currentItems, payload);
      setReceiving({ ...receiving, items: newItems });
      showSuccess(RECEIVING_MESSAGES.itemDeleted);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showError(RECEIVING_MESSAGES.itemDeleteFailed);
    }
  };

  const deleteReceivingItemByBarcode = async function (barcode) {
    let callResult = await makePostApiCall(
      `deleteReceivingItemByBarcode/${barcode}`,
    );
    if (callResult.success) {
      setEmptyModelOpen({
        open: false,
        childComponent: null,
      });

      const deletedItem = callResult.data.item;
      const currentItems = [...receiving.items];

      var itemIndex = currentItems.findIndex(
        (it) =>
          it.plu === deletedItem.plu &&
          it.lot_number === deletedItem.lot_number,
      );
      var itemToUpdate = currentItems[itemIndex];

      const newSubItems = itemToUpdate.subItems.filter(
        (it) => it.id !== deletedItem.id,
      );

      itemToUpdate = {
        ...itemToUpdate,
        carton_count: itemToUpdate.carton_count - 1,
        subItems: newSubItems,
      };

      var payload = {
        index: itemIndex,
        item: itemToUpdate,
      };
      const newItems = updateObjectInArray(currentItems, payload);
      setReceiving({ ...receiving, items: newItems });

      showSuccess(RECEIVING_MESSAGES.itemDeleted);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showError(RECEIVING_MESSAGES.itemDeleteFailed);
    }
  };

  const askDeleteReceivingItemById = function (item) {
    setEmptyModelOpen({
      childComponent: (
        <DeleteConfirmationForm
          buttonMessage="Delete"
          confirmAction={() => deleteReceivingItemById(item)}
          message={`Are you sure you want to return  ${item.product_name}?`}
          setOpen={setEmptyModelOpen}
        />
      ),
      open: true,
    });
  };

  const updateReceivingItem = async function (payload) {
    let callResult = await makePutApiCall(
      `updateReceivingItem/${payload.id}`,
      payload,
    );
    if (callResult.success) {
      setEmptyModelOpen({
        open: false,
        childComponent: null,
      });

      showSuccess(RECEIVING_MESSAGES.itemUpdated);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showError(RECEIVING_MESSAGES.itemUpdateFailed);
    }
  };

  const openDeleteItemByBarcodePopup = function () {
    setEmptyModelOpen({
      childComponent: (
        <DeleteReceivingItemByBarcodeForm
          barcodeOptions={receiving.barcodes}
          buttonMessage="Delete"
          confirmAction={(barcode) => deleteReceivingItemByBarcode(barcode)}
          setOpen={setEmptyModelOpen}
        />
      ),
      open: true,
    });
  };

  const getWarehouses = async function () {
    let callResult = await makeGetApiCall("warehousesForDropdown");
    if (!callResult.success) {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showError(RECEIVING_MESSAGES.loadWarehousesFailed);
    }
    return callResult;
  };

  const getWarehouseLocations = async function (warehouseId) {
    let callResult = await makeGetApiCall(
      `warehouseLocationsForDropdown/${warehouseId}`,
    );
    if (!callResult.success) {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showError(RECEIVING_MESSAGES.loadLocationsFailed);
    }
    return callResult;
  };

  const onDeleteReceivingInvoice = async function () {
    let callResult = await makePostApiCall(
      `deleteReceivingInvoice/${receiving.id}`,
    );
    if (callResult.success) {
      showSuccess(RECEIVING_MESSAGES.deleted);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showError(RECEIVING_MESSAGES.deleteFailed);
    }
    return callResult;
  };

  const deleteReceivingInvoiceDocument = async function (field) {
    let callResult = await makePostApiCall(
      `deleteReceivingInvoiceDocument/${receiving.id}/${field}`,
    );
    if (callResult.success) {
      showSuccess(RECEIVING_MESSAGES.documentDeleted);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showError(RECEIVING_MESSAGES.documentDeleteFailed);
    }
    return callResult;
  };

  const onDeleteSelectedReceivingItems = async function (reason) {
    let callResult = await makePostApiCall(`deleteReceivingItemsByMultiple`, {
      receiving_id: receiving.id,
      items: selectedItems,
      reason: reason,
    });
    if (callResult.success) {
      setEmptyModelOpen({
        open: false,
        childComponent: null,
      });
      const newItems = callResult.data.receiving_items;

      setReceiving({ ...receiving, items: newItems });
      setSelectedItems(null);

      showSuccess(RECEIVING_MESSAGES.itemsDeleted);

      if (selectedItems.length > 0) {
        const firstItem = selectedItems[0];
        fetchRowDetails({
          unique_id: firstItem.parent_unique_id,
          plu: firstItem.plu,
          lot_number: firstItem.lot_number,
          warehouse_location_id: firstItem.warehouse_location_id,
        });
      }
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showError(RECEIVING_MESSAGES.itemsDeleteFailed);
    }
  };

  const askReturnSelectedItems = () => {
    if (receiving.status === 1) {
      showError(RECEIVING_MESSAGES.closedDelete);
    } else {
      setEmptyModelOpen({
        childComponent: (
          <DeleteConfirmationForm
            buttonMessage="Delete"
            confirmAction={(reason) => onDeleteSelectedReceivingItems(reason)}
            message={`Are you sure you want to return these items?`}
            setOpen={setEmptyModelOpen}
          />
        ),
        open: true,
      });
    }
  };

  const onUpdateUnitCostForReceivingItem = async function (payload) {
    let callResult = await makePostApiCall(`receivingItems/updateUnitCost`, {
      ...payload,
      receiving_id: receiving.id,
    });
    if (callResult.success) {
      setEmptyModelOpen({
        open: false,
        childComponent: null,
      });
      const newItems = callResult.data.receiving_items;

      setReceiving({ ...receiving, items: newItems });
      showSuccess(RECEIVING_MESSAGES.unitCostUpdated);

      if (rowDetails[payload.unique_id]) {
        fetchRowDetails(payload);
      }
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showError(RECEIVING_MESSAGES.costUpdateFailed);
    }
  };

  const onUpdateTotalCostForReceivingItem = async function (payload) {
    let callResult = await makePostApiCall(`receivingItems/updateTotalCost`, {
      ...payload,
      receiving_id: receiving.id,
    });
    if (callResult.success) {
      setEmptyModelOpen({
        open: false,
        childComponent: null,
      });
      const newItems = callResult.data.receiving_items;

      setReceiving({ ...receiving, items: newItems });
      showSuccess(RECEIVING_MESSAGES.totalCostUpdated);

      if (rowDetails[payload.unique_id]) {
        fetchRowDetails(payload);
      }
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showError(RECEIVING_MESSAGES.costUpdateFailed);
    }
  };

  const printReceivingSummaryReport = async function () {
    let callResult = await makePostApiCall(
      `generateReceivingSummaryReport/${receivingId}`,
    );

    if (callResult.success) {
      window.open(callResult.data.location, "_blank");
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showError(RECEIVING_MESSAGES.reportFailed);
    }
    return callResult;
  };

  const printReceivingDetailsReport = async function () {
    let callResult = await makePostApiCall(
      `generateReceivingDetailsReport/${receivingId}`,
    );

    if (callResult.success) {
      window.open(callResult.data.location, "_blank");
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showError(RECEIVING_MESSAGES.reportFailed);
    }
    return callResult;
  };

  const fetchRowDetails = async (rowData) => {
    if (
      !rowData?.plu ||
      !rowData?.lot_number ||
      rowData?.warehouse_location_id === undefined ||
      rowData?.warehouse_location_id === null ||
      !rowData?.unique_id
    ) {
      return;
    }

    setLoadingRows((prev) => ({ ...prev, [rowData.unique_id]: true }));

    let callResult = await makeGetApiCall(
      `receivingItems/${receivingId}/${encodeURIComponent(rowData.plu)}/${encodeURIComponent(rowData.lot_number)}/${rowData.warehouse_location_id}/${encodeURIComponent(rowData.unique_id)}`,
    );
    if (callResult.success) {
      setRowDetails({ ...rowDetails, [rowData.unique_id]: callResult.data });
      setLoadingRows({ ...loadingRows, [rowData.unique_id]: false });
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      setLoadingRows({ ...loadingRows, [rowData.unique_id]: false });
    }
  };

  const fetchSubRowDetails = async (rowData) => {
    setLastRow(rowData);
    setSubLoadingRows((prev) => ({ ...prev, [rowData.unique_id]: true }));

    let callResult = await makeGetApiCall(
      `receivingItems/pallets/${rowData.pallet_id}`,
    );
    if (callResult.success) {
      setSubRowDetails({
        ...subRowDetails,
        [rowData.unique_id]: callResult.data,
      });
      setSubLoadingRows({ ...subLoadingRows, [rowData.unique_id]: false });
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      setSubLoadingRows({ ...subLoadingRows, [rowData.unique_id]: false });
    }
  };

  const getCarriersForDropdown = async function () {
    let callResult = await makeGetApiCall(`carriersForDropdown`);
    if (callResult.success) {
      setCarrierOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showError(RECEIVING_MESSAGES.loadCarriersFailed);
    }
  };

  return {
    emptyModal,
    setEmptyModelOpen,
    truckOptions,
    getTruckOptionsForDropdown,
    supplierOptions,
    getSupplierOptionsForDropdown,
    getProductOptionsForDropdown,
    productOptions,
    onFetchProductDetails,
    poOptions,
    receiving,
    addItemToReceiving,
    askDeleteReceivingItemById,
    updateReceivingItem,
    openDeleteItemByBarcodePopup,
    updateReceivingInvoice,
    getReceivingInvoiceDetails,
    employeeOptions,
    getEmployeeOptionsForDropdown,
    getWarehouses,
    getWarehouseLocations,
    onDeleteReceivingInvoice,
    deleteReceivingInvoiceDocument,
    askReturnSelectedItems,
    selectedItems,
    setSelectedItems,
    onUpdateUnitCostForReceivingItem,
    onUpdateTotalCostForReceivingItem,
    printReceivingSummaryReport,
    printReceivingDetailsReport,
    loadingRows,
    rowDetails,
    fetchRowDetails,
    fetchSubRowDetails,
    subRowDetails,
    subLoadingRows,
    getCarriersForDropdown,
    carrierOptions,
    loading,
    setLoading,
  };
}
